import type { AdjustPlatform } from "./platform";

/**
 * プラットフォーム別 Adjust イベントトークン。
 * 定義されていないイベント・プラットフォームの組み合わせは送信しない（ホワイトリスト方式）。
 *
 * CarinaIos 列は 2026-08 のイベント刷新後のトークン。FE イベント名と Adjust
 * ダッシュボード上の登録名が異なる場合は各行のコメントに登録名を記す。
 *
 * CocoplusIos（coco+ / applicationId=145）は 2026-10 のイベント刷新後の小文字名トークンに揃える（Carina と同じ命名方針）。
 */
export const ADJUST_TOKENS: Record<
  string,
  Partial<Record<AdjustPlatform, string>>
> = {
  // ネイティブ課金完了を purchaseEvent で受け取った際に送信する（Corun / coco+）
  purchased: { CorunIos: "txpqzn", CocoplusIos: "u55mjm" }, // revenue 付き
  completePurchasePoint: { CorunIos: "a91dvp", CocoplusIos: "d66a9j" },
  login: { HibanaIos: "st77f7", CocoplusIos: "is2nnn", CorunIos: "f8gvm3" },
  sign_up: {
    RenkaAndroid: "x3qjkl",
    HokageIos: "ydkl5l",
    CarinaIos: "b2cglx", // register
    SakuraIos: "orzovn",
    HibanaIos: "vmy5n0",
    CocoplusIos: "fyypn4",
    CorunIos: "1au93b",
  },
  edit_profile: {
    RenkaAndroid: "74pbz6",
    HokageIos: "fiverv",
    CarinaIos: "5qboys",
    SakuraIos: "dwituj",
    CocoplusIos: "w39hcn",
  },
  COMPLETE_SEND_MESSAGE: {
    RenkaAndroid: "su7ub9",
    HokageIos: "k49iu2",
    CarinaIos: "fhxi7p", // send_message
    SakuraIos: "bnpxzd",
    CocoplusIos: "tuyuz5",
  },
  COMPLETE_SEND_ONE_TAP_MESSAGE_FROM_BOARD: {
    RenkaAndroid: "jr9d8f",
    HokageIos: "dmcuyy",
    CarinaIos: "ur53h3", // board_onetap
    SakuraIos: "5poajo",
    CocoplusIos: "pft1pn",
  },
  COMPLETE_SEND_FREE_MESSAGE_FROM_BOARD: {
    RenkaAndroid: "lqe0e4",
    HokageIos: "t0zq52",
    CarinaIos: "1rvkqc", // board_message
    SakuraIos: "ugfsby",
    CocoplusIos: "u8cw84",
  },
  SEND_GOOD: {
    RenkaAndroid: "bxgz31",
    HokageIos: "97zzxy",
    CarinaIos: "3vfic4", // add_like
    SakuraIos: "71yu13",
    CocoplusIos: "z50jz2",
  },
  // 接続成立（課金開始0秒）時点。発信は OUTGOING_* 側で videocall_start / voicecall_start を送る
  START_VIDEO_CALL: {
    RenkaAndroid: "7fapxn",
    HokageIos: "p3s6hl",
    CarinaIos: "9u3v8y", // videocall_connect
    SakuraIos: "hni7zu",
    HibanaIos: "898p1w",
    CocoplusIos: "gqz07k",
  },
  START_VIDEO_CHAT: {
    RenkaAndroid: "9vnh8e",
    HokageIos: "79sddh",
    CarinaIos: "njrz9w", // videochat_start
    SakuraIos: "8mav3l",
    HibanaIos: "wh8xn4",
    CocoplusIos: "7qncvv",
  },
  START_VOICE_CALL: {
    RenkaAndroid: "3dglom",
    HokageIos: "xj43uw",
    CarinaIos: "9x72eo", // voicecall_connect
    SakuraIos: "2cric4",
    HibanaIos: "5fhfcd",
    CocoplusIos: "y1kr6z",
  },
  COMPLETE_BUY_VIDEO: {
    RenkaAndroid: "50iu1f",
    HokageIos: "yn9fqs",
    CarinaIos: "p322bi", // view_video
    SakuraIos: "x3kgyb",
    HibanaIos: "tvwlt6",
    CocoplusIos: "y2l063",
  },
  COMPLETE_BUY_IMAGE: {
    RenkaAndroid: "789ltc",
    HokageIos: "toq8eh",
    CarinaIos: "5ghpzr", // view_image
    SakuraIos: "uyonmu",
    HibanaIos: "eiadpx",
    CocoplusIos: "uliuj5",
  },
  COMPLETE_SEND_GIFT: {
    RenkaAndroid: "5c3bs0",
    HokageIos: "mcs1po",
    CarinaIos: "t7nwjg", // send_gift
    SakuraIos: "gzxzqj",
    CocoplusIos: "v8hb8m",
  },
  COMPLETE_SEND_STAMP: {
    RenkaAndroid: "22cxw5",
    HokageIos: "idzhwu",
    CarinaIos: "xwf0ex", // send_sticker
    SakuraIos: "yyw9fi",
    CocoplusIos: "j8n7kg",
  },
  use_signup_point: {
    RenkaAndroid: "uorvou",
    HokageIos: "li2i9u",
    CarinaIos: "hgzjw1",
    SakuraIos: "85zwfy",
    HibanaIos: "sxr9ji",
    CocoplusIos: "vazdjm",
  },
  // Sakura はメニュー種別を区別しないため通常送信・完全コントロールとも同一トークン
  SEND_LOVENSE_IN_VIDEO_CALL: {
    SakuraIos: "xznuk4",
    CarinaIos: "my0vif", // send_video_lovense
    HibanaIos: "94zms5",
    CocoplusIos: "jmdtpf",
  },
  SEND_LOVENSE_IN_VIDEO_CHAT: {
    SakuraIos: "rcjilf",
    CarinaIos: "vth9bd", // send_videochat_lovense
    HibanaIos: "ttaj51",
    CocoplusIos: "islugo",
  },
  send_video_complete_lovense: {
    SakuraIos: "xznuk4",
    CarinaIos: "y1c7qw",
    CocoplusIos: "g6kza4",
  },
  send_videochat_complete_lovense: {
    SakuraIos: "rcjilf",
    CarinaIos: "m0ggsp",
    CocoplusIos: "oqjoqw",
  },
  // --- 以下は Carina のみ送信 ---
  // 通話・ライブ
  OUTGOING_VIDEO_CALL: { CarinaIos: "cj37bq" }, // videocall_start
  OUTGOING_VOICE_CALL: { CarinaIos: "kuim7b" }, // voicecall_start
  INCOMING_VIDEO_CALL: {
    CarinaIos: "mjanxj",
    HibanaIos: "cj2sz3",
    CocoplusIos: "w18c4x",
  }, // videocall_incoming
  INCOMING_VOICE_CALL: {
    CarinaIos: "p4vju2",
    HibanaIos: "4cpwxg",
    CocoplusIos: "auwc65",
  }, // voicecall_incoming
  videocall_end: {
    CarinaIos: "auvfyx",
    HibanaIos: "1p82kj",
    CocoplusIos: "bcnhzv",
  },
  videochat_end: {
    CarinaIos: "diqd4j",
    HibanaIos: "ozc392",
    CocoplusIos: "no3qst",
  },
  // HIBANA のみに存在する接続成立イベント。START_VIDEO_CALL/START_VOICE_CALL とは別イベント。呼び出し元は未実装（要実装確認）
  videocall_connect: { HibanaIos: "x8izad", CocoplusIos: "5viulc" },
  voicecall_connect: { HibanaIos: "xvd9xi", CocoplusIos: "jv1cho" },
  SEND_MESSAGE_IN_VIDEO_CHAT: {
    CarinaIos: "acy9j8",
    HibanaIos: "douedb",
    CocoplusIos: "w2wdp8",
  }, // videochat_send_message
  // リクエスト
  SEND_VIDEO_CALL_REQUEST: { CarinaIos: "pdfhcb", CocoplusIos: "5oe5y8" }, // request_videocall
  SEND_VOICE_CALL_REQUEST: { CarinaIos: "dku7he", CocoplusIos: "px0pck" }, // request_voicecall
  SEND_IMAGE_FILE_REQUEST: { CarinaIos: "tw6r0m", CocoplusIos: "ffbrzm" }, // request_image
  SEND_VIDEO_FILE_REQUEST: { CarinaIos: "wdd4sv", CocoplusIos: "a8qqr8" }, // request_video
  // タップ・プロフィールアクション
  ADD_FAVORITE: { CarinaIos: "slp1cg", CocoplusIos: "z1x3h2" }, // add_favorite
  remove_favorite: { CarinaIos: "49x8yq", CocoplusIos: "7jdid4" },
  upload_profile_avatar: { CarinaIos: "5zl26m", CocoplusIos: "atv7rl" }, // add_avatar
  add_about_me: { CarinaIos: "uf8atj", CocoplusIos: "hc43qi" },
  add_age: { CarinaIos: "wh02rh", CocoplusIos: "q9eg57" },
  get_dailybonus: { CarinaIos: "lmorji", CocoplusIos: "sv3wkp" },
  post_review: { CarinaIos: "b8nwec", CocoplusIos: "64dc0k" },
  // 検索
  tap_search: {
    CarinaIos: "3letss",
    HibanaIos: "voauww",
    CocoplusIos: "jpyn9h",
  },
  search_name: { CarinaIos: "yync7x", CocoplusIos: "mv8ovj" },
  search_body: { CarinaIos: "qveles", CocoplusIos: "mgareo" },
  search_age: { CarinaIos: "4zyrpd", CocoplusIos: "6ah795" },
  search_location: { CarinaIos: "vw7nva", CocoplusIos: "c7ay9m" },
  search_videocall: { CarinaIos: "kh030f", CocoplusIos: "4qskkz" },
  // フリマ
  open_fleamarket_item: { CarinaIos: "i4neiw", CocoplusIos: "64feac" },
  view_fleamarket_item_from_profile: {
    CarinaIos: "4jj446",
    HibanaIos: "8isneu",
    CocoplusIos: "nvksl6",
  },
  open_profile_from_fleamarket_item: {
    CarinaIos: "7n852a",
    CocoplusIos: "veqhjd",
  },
  favorite_fleamarket_item: { CarinaIos: "acv1qt", CocoplusIos: "wxcjtn" },
  purchased_fleamarket_item: { CarinaIos: "3bz4tm", CocoplusIos: "g6pebv" },
  // 通話・ライブ配信中のフリマ出品バナータップ。呼び出し元は call/live 側で未実装（要実装確認）
  tap_fleamarket_item_videocall: { HibanaIos: "q47pwq", CocoplusIos: "tqe0di" },
  tap_fleamarket_item_videochat: { HibanaIos: "h2qqbg", CocoplusIos: "nz57dh" },
  // 画面表示
  OPEN_GIRLS_ALL: { CarinaIos: "pip0ir", CocoplusIos: "tcejfl" }, // open_home
  OPEN_SEARCH: { CarinaIos: "lzo88w", CocoplusIos: "saypj0" }, // open_search
  OPEN_BOARD: { CarinaIos: "bxnbgr", CocoplusIos: "1gzel5" }, // open_board
  OPEN_GALLERY: { CarinaIos: "ui2be9", CocoplusIos: "gk7ux5" }, // open_gallery
  OPEN_FOOTPRINT_LIST: { CarinaIos: "s2f8rf", CocoplusIos: "41gux0" }, // open_footstep
  OPEN_BOOKMARK_LIST: { CarinaIos: "esf7u9", CocoplusIos: "saqecr" }, // open_favorite
  OPEN_LIKE_LIST: { CarinaIos: "zdf7u5", CocoplusIos: "1ova1o" }, // open_like
  OPEN_PROFILE_DETAIL: { CarinaIos: "488m6n", CocoplusIos: "ftq0ub" }, // open_profile
  OPEN_FLEA_MARKET: { CarinaIos: "w09i0a", CocoplusIos: "8c4y1i" }, // open_fleamarket
};

export const getAdjustToken = (
  eventName: string,
  platform: AdjustPlatform,
): string | undefined => {
  return ADJUST_TOKENS[eventName]?.[platform];
};
